import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO, subMonths } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { QBO_CHANGE_LEDGER } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';

const PAGE_SIZE = 50;
const STATEMENTS = [
  { value: 'pl', label: 'P&L accounts' },
  { value: 'bs', label: 'Balance sheet accounts' },
  { value: 'all', label: 'All accounts' },
];
const ENTITY_TYPES = [
  'JournalEntry', 'Bill', 'BillPayment', 'Purchase', 'Deposit', 'Invoice',
  'SalesReceipt', 'Payment', 'CreditMemo', 'VendorCredit', 'Transfer', 'RefundReceipt',
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const currency = (value) => (value === null || value === undefined || value === ''
  ? '—'
  : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const signed = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return `${n > 0 ? '+' : n < 0 ? '-' : ''}${currency(Math.abs(n))}`;
};
const formatValue = (value, column) => {
  if (value === null || value === undefined || value === '') return '—';
  if (column.type === 'currency') {
    return ['delta', 'net_income_impact'].includes(column.key) ? signed(value) : currency(value);
  }
  if (column.type === 'date') {
    const parsed = parseISO(String(value).slice(0, 10));
    return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'MM/dd/yyyy');
  }
  return value;
};
const deltaClass = (value) => {
  const n = Number(value);
  if (!n) return '';
  return n < 0 ? 'text-red-600' : 'text-green-700';
};
const monthLabel = (month) => {
  const parsed = parseISO(`${month}-01`);
  return Number.isNaN(parsed.getTime()) ? month : format(parsed, 'MMMM yyyy');
};

const QboChangeLedgerReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const defaultMonth = format(subMonths(new Date(), 1), 'yyyy-MM');
  const month = searchParams.get('month') || defaultMonth;
  const statement = searchParams.get('statement') || 'pl';
  const entityType = searchParams.get('entityType') || 'all';
  const closedOnly = searchParams.get('closedOnly') !== '0';
  const account = searchParams.get('account') || '';
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totals, setTotals] = useState({});
  const [byAccount, setByAccount] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const setParam = (key, value, defaultValue) => {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === null || value === '' || value === defaultValue) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const params = useCallback((includePage = true) => {
    const query = new URLSearchParams({ month, statement });
    if (includePage) {
      query.set('page', page);
      query.set('pageSize', PAGE_SIZE);
    }
    if (entityType !== 'all') query.set('entityType', entityType);
    if (!closedOnly) query.set('closedOnly', '0');
    if (account) query.set('account', account);
    if (search) query.set('search', search);
    return query;
  }, [month, statement, page, entityType, closedOnly, account, search]);

  const load = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/reports/${QBO_CHANGE_LEDGER}?${params()}`, { headers: authHeaders(), signal });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load report');
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setColumns(json.columns || []);
      setTotals(json.totals || {});
      setByAccount(json.byAccount || []);
      setTotalRecords(json.totalRecords || 0);
      setTotalPages(json.totalPages || 1);
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !isLatest(requestId)) return;
      setError(requestError.message);
      setData([]);
    } finally {
      if (isLatest(requestId)) setLoading(false);
    }
  }, [beginRequest, isLatest, params]);

  useEffect(() => {
    if (initialReady) load();
  }, [initialReady, load]);

  const exportData = async () => {
    const response = await fetch(`/api/v1/reports/${QBO_CHANGE_LEDGER}/export?${params(false)}`, { headers: authHeaders() });
    if (!response.ok) throw new Error('Failed to export report');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qbo-change-ledger-${month}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  };

  const filters = (
    <div className="contents">
      <input
        type="month"
        aria-label="Books month"
        className="rounded border px-3 py-2 text-sm"
        value={month}
        onChange={(event) => setParam('month', event.target.value, defaultMonth)}
      />
      <select className="rounded border px-3 py-2 text-sm" value={statement} onChange={(event) => setParam('statement', event.target.value, 'pl')}>
        {STATEMENTS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      <select className="rounded border px-3 py-2 text-sm" value={entityType} onChange={(event) => setParam('entityType', event.target.value, 'all')}>
        <option value="all">All transaction types</option>
        {ENTITY_TYPES.map((value) => <option key={value} value={value}>{value}</option>)}
      </select>
      <input
        className="rounded border px-3 py-2 text-sm"
        placeholder="Doc #, name or QBO id"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onBlur={() => setParam('search', search, '')}
        onKeyDown={(event) => { if (event.key === 'Enter') setParam('search', search, ''); }}
      />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={closedOnly} onChange={(event) => setParam('closedOnly', event.target.checked ? '' : '0', '')} />
        Changes after close only
      </label>
      {account && (
        <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setParam('account', '', '')}>
          Clear account filter
        </button>
      )}
    </div>
  );

  const pagination = (
    <div className="flex items-center justify-between text-sm">
      <span>{totalRecords.toLocaleString()} account lines</span>
      <div className="flex gap-2">
        <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-50">Previous</button>
        <span className="py-1">Page {page} of {totalPages}</span>
        <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-50">Next</button>
      </div>
    </div>
  );

  const cards = [
    ['net_income_impact', `${monthLabel(month)} net income impact`, signed, deltaClass(totals.net_income_impact)],
    ['pl_delta', 'P&L debit delta', signed, ''],
    ['bs_delta', 'Balance sheet debit delta', signed, ''],
    ['transactions', 'Transactions changed', (v) => Number(v || 0).toLocaleString(), ''],
  ];

  return (
    <ReportTemplate
      reportId={QBO_CHANGE_LEDGER}
      title="QBO Change Ledger"
      description="QuickBooks transactions created, edited or deleted after a month's books were closed, with per-account before/after and the resulting net income impact."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      filters={filters}
      exportButton={<CSVExportButton data={data} columns={columns} filename={`qbo-change-ledger-${month}`} onExport={exportData} />}
      pagination={pagination}
      renderChildrenOnly
    >
      <div className="space-y-3">
        <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4">
          {cards.map(([key, label, fmt, cls]) => (
            <div key={key} className="rounded bg-gray-50 p-3">
              <div className="text-xs text-gray-500">{label}</div>
              <div className={`font-semibold ${cls}`}>{fmt(totals[key])}</div>
            </div>
          ))}
        </div>

        <div className="rounded border bg-white">
          <div className="border-b px-4 py-2 text-sm font-semibold">By account</div>
          {byAccount.length === 0 ? <div className="px-6 py-6 text-center text-sm text-gray-500">No account changes for the selected filters.</div> : (
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2">Account</th>
                  <th className="px-3 py-2">Acct #</th>
                  <th className="px-3 py-2">Statement</th>
                  <th className="px-3 py-2 text-right">Before</th>
                  <th className="px-3 py-2 text-right">After</th>
                  <th className="px-3 py-2 text-right">Delta (debit +)</th>
                  <th className="px-3 py-2 text-right">Net income impact</th>
                  <th className="px-3 py-2 text-right">Txns</th>
                </tr>
              </thead>
              <tbody>
                {byAccount.map((row, index) => (
                  <tr
                    key={row.account_key}
                    className={`cursor-pointer hover:bg-blue-50 ${account === row.account_key ? 'bg-blue-50' : index % 2 ? 'bg-gray-50' : ''}`}
                    onClick={() => setParam('account', account === row.account_key ? '' : row.account_key, '')}
                    title="Click to filter transactions to this account"
                  >
                    <td className="px-3 py-2">{row.account_name || row.account_key}</td>
                    <td className="px-3 py-2">{row.acct_num || '—'}</td>
                    <td className="px-3 py-2">{row.statement}</td>
                    <td className="px-3 py-2 text-right">{currency(row.before)}</td>
                    <td className="px-3 py-2 text-right">{currency(row.after)}</td>
                    <td className={`px-3 py-2 text-right ${deltaClass(row.delta)}`}>{signed(row.delta)}</td>
                    <td className={`px-3 py-2 text-right ${deltaClass(row.net_income_impact)}`}>{row.net_income_impact === null || row.net_income_impact === undefined ? '—' : signed(row.net_income_impact)}</td>
                    <td className="px-3 py-2 text-right">{row.changes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="overflow-auto rounded border bg-white">
          <div className="border-b px-4 py-2 text-sm font-semibold">Transactions</div>
          {data.length === 0 ? <div className="px-6 py-12 text-center text-sm text-gray-500">No QBO changes match the selected filters.</div> : (
            <table className="min-w-max text-left text-xs">
              <thead className="sticky top-0 bg-gray-100"><tr>{columns.map((column) => <th key={column.key} className="whitespace-nowrap px-3 py-2">{column.label}</th>)}</tr></thead>
              <tbody>{data.map((row, index) => <tr key={`${row.change_id}-${row.account_key || index}`} className={index % 2 ? 'bg-gray-50' : ''}>
                {columns.map((column) => (
                  <td key={column.key} className={`whitespace-nowrap px-3 py-2 ${['delta', 'net_income_impact'].includes(column.key) ? `text-right ${deltaClass(row[column.key])}` : column.type === 'currency' ? 'text-right' : ''}`}>
                    {formatValue(row[column.key], column)}
                  </td>
                ))}
              </tr>)}</tbody>
            </table>
          )}
        </div>
      </div>
    </ReportTemplate>
  );
};

export default QboChangeLedgerReport;
